export interface WorkOrderStatusInfo {
  label: string;
  color: string;
  isActive: boolean;
}

/**
 * Canonical work-order status registry — the single source of truth for
 * work-order status labels, colors, and active/inactive classification
 * across web and mobile.
 *
 * Colors are the canonical palette taken from the most-seen live surfaces
 * (web WorkOrderDetail/WorkOrders, mobile WorkOrderDetailModal).
 */
export const WORK_ORDER_STATUSES: Record<string, WorkOrderStatusInfo> = {
  // Early/legacy state used as a frontend normalization fallback
  open: { label: 'Open', color: '#64B5F6', isActive: false },
  // Backend states (see turn-connect internal/domain/entities/work_order.go)
  pre_approved: { label: 'Pre Approved', color: '#64B5F6', isActive: true },
  inspection: { label: 'Inspection', color: '#8B5CF6', isActive: true },
  awaiting_approval: { label: 'Awaiting Approval', color: '#5F99FF', isActive: true },
  in_progress: { label: 'In Progress', color: '#FF9800', isActive: true },
  closed: { label: 'Closed', color: '#4CAF50', isActive: false },
  declined: { label: 'Declined', color: '#EF4444', isActive: false },
  // Legacy/derived states still seen in frontend data
  pending: { label: 'Pending', color: '#2196F3', isActive: false },
  completed: { label: 'Completed', color: '#4CAF50', isActive: false },
  deleted: { label: 'Deleted', color: '#EF4444', isActive: false },
};

/** Fallback color for unknown/missing work-order statuses. */
const UNKNOWN_WORK_ORDER_STATUS_COLOR = '#6B7280';

/**
 * Active statuses that should be shown on the work orders page (excludes 'closed').
 * Closed work orders should only be visible on reports.
 * Must match dashboard_service.go:531 which counts pre_approved,inspection,awaiting_approval,in_progress
 */
export const ACTIVE_WORK_ORDER_STATUSES: string[] = Object.keys(WORK_ORDER_STATUSES).filter(
  (status) => WORK_ORDER_STATUSES[status].isActive
);

/** Normalize a status for registry lookup: case-insensitive, spaces/hyphens → underscores. */
function normalizeWorkOrderStatus(status: string | null | undefined): string {
  if (!status) return '';
  return status
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function lookupWorkOrderStatus(status: string | null | undefined): WorkOrderStatusInfo | undefined {
  return WORK_ORDER_STATUSES[normalizeWorkOrderStatus(status)];
}

/**
 * Get the canonical color for a work-order status.
 * Lookup is case-insensitive and tolerates space/hyphen separators
 * (e.g. 'In Progress' resolves to 'in_progress'). Unknown statuses get gray.
 */
export function getWorkOrderStatusColor(status: string | null | undefined): string {
  return lookupWorkOrderStatus(status)?.color ?? UNKNOWN_WORK_ORDER_STATUS_COLOR;
}

/**
 * Get the canonical display label for a work-order status.
 * Unknown statuses fall back to generic `formatStatus` formatting.
 */
export function getWorkOrderStatusLabel(status: string | null | undefined): string {
  return lookupWorkOrderStatus(status)?.label ?? formatStatus(normalizeWorkOrderStatus(status));
}

/**
 * Whether a work-order status counts as "active" (shown on the work orders
 * page / dashboards). Matches ACTIVE_WORK_ORDER_STATUSES.
 */
export function isActiveWorkOrderStatus(status: string | null | undefined): boolean {
  return lookupWorkOrderStatus(status)?.isActive ?? false;
}

export function formatStatus(status: string | null | undefined): string {
  if (!status) return '';

  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get status color for consistent badge/indicator styling.
 *
 * @param status - Status string
 * @param variant - Status type (work-order, user, location, etc.)
 * @returns Color hex code
 *
 * @example
 * ```typescript
 * getStatusColor('completed', 'work-order');
 * // Returns: "#4CAF50"
 * ```
 */
export function getStatusColor(
  status: string | null | undefined,
  variant: 'work-order' | 'user' | 'location' | 'generic' = 'generic'
): string {
  if (!status) return '#666666';

  const statusLower = status.toLowerCase();

  // Work order statuses — delegate to the canonical registry
  if (variant === 'work-order') {
    return getWorkOrderStatusColor(statusLower);
  }

  // User statuses
  if (variant === 'user') {
    switch (statusLower) {
      case 'active':
        return '#4CAF50'; // Green
      case 'inactive':
        return '#666666'; // Gray
      case 'suspended':
        return '#F44336'; // Red
      case 'pending':
        return '#FF9800'; // Orange
      default:
        return '#666666'; // Gray
    }
  }

  // Location statuses
  if (variant === 'location') {
    switch (statusLower) {
      case 'active':
        return '#4CAF50'; // Green
      case 'inactive':
        return '#666666'; // Gray
      case 'maintenance':
        return '#FF9800'; // Orange
      default:
        return '#666666'; // Gray
    }
  }

  // Generic statuses
  switch (statusLower) {
    case 'success':
    case 'completed':
    case 'active':
    case 'approved':
      return '#4CAF50'; // Green
    case 'warning':
    case 'pending':
    case 'in_progress':
    case 'in progress':
      return '#FF9800'; // Orange
    case 'error':
    case 'failed':
    case 'rejected':
    case 'cancelled':
    case 'canceled':
      return '#F44336'; // Red
    case 'info':
    case 'draft':
      return '#2196F3'; // Blue
    case 'inactive':
    case 'disabled':
      return '#666666'; // Gray
    default:
      return '#666666'; // Gray
  }
}
